import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import {
  DropdownEntry,
  DropdownGroup,
} from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { TableHeader, TableHeaderIcons } from '@design/components/table';
import { flexRender, Header, SortingState } from '@tanstack/react-table';
import { EarningsTableItem } from '@terminal/views/theme-overview/tab-content/earnings/Earnings';
import clsx from 'clsx';
import { Property } from 'csstype';
import { useTranslation } from 'react-i18next';

import { SCHEDULED_DATE_COLUMN_ID } from '../../utils/getColumnUtils';
import styles from './EarningsTableHeader.module.scss';

const DATE_HEADERS_IDS = [SCHEDULED_DATE_COLUMN_ID];

export const EarningsTableHeaderCell = ({
  header,
  isLastHeaderGroup,
  isHighlighted,
  setActiveSortingState,
}: {
  header: Header<EarningsTableItem, unknown>;
  isLastHeaderGroup: boolean;
  isHighlighted: boolean;
  setActiveSortingState: (sortingState: SortingState) => void;
}) => {
  const { t } = useTranslation('widget');

  const isDateHeader = DATE_HEADERS_IDS.includes(header.column.id);
  const sortOptions: DropdownEntry[] = [
    {
      label: t('widget:earnings:table_ascending'),
      iconName: 'SortUp',
      key: 'sortUp',
      onClick: () =>
        setActiveSortingState([
          {
            id: header.column.id,
            desc: false,
          },
        ]),
    },
    {
      label: t('widget:earnings:table_descending'),
      iconName: 'SortDown',
      key: 'sortDown',
      onClick: () =>
        setActiveSortingState([
          {
            id: header.column.id,
            desc: true,
          },
        ]),
    },
  ];
  const sortDateOptions: DropdownEntry[] = [
    {
      label: t('widget:earnings:newestFirst'),
      iconName: 'SortDown',
      key: 'SortDown',
      onClick: () =>
        setActiveSortingState([
          {
            id: header.column.id,
            desc: true,
          },
        ]),
    },
    {
      label: t('widget:earnings:oldestFirst'),
      iconName: 'SortUp',
      key: 'SortUp',
      onClick: () =>
        setActiveSortingState([
          {
            id: header.column.id,
            desc: false,
          },
        ]),
    },
  ];

  const sortOrder = header.column.getIsSorted();

  const tableHeader = (
    <TableHeader
      key={header.id}
      colSpan={header.colSpan}
      alignment={
        (!isLastHeaderGroup
          ? 'center'
          : header.index > 2
            ? 'right'
            : 'left') as Property.TextAlign
      }
      className={clsx(
        styles.tableHeader,
        isLastHeaderGroup ? styles.lastHeaderGroup : styles.notLastHeaderGroup,
        isHighlighted && styles.highlighted,
        header.column.getCanSort() && styles.sortable
      )}
    >
      <TableHeaderIcons
        isFiltered={header.column.getIsFiltered()}
        sortDirection={sortOrder}
        alignment="left"
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
      </TableHeaderIcons>
    </TableHeader>
  );

  if (header.column.getCanSort()) {
    return (
      <DropdownMenu key={header.id} triggerNode={tableHeader}>
        <DropdownGroup items={isDateHeader ? sortDateOptions : sortOptions} />
      </DropdownMenu>
    );
  }

  return tableHeader;
};
